import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { recordReferralCommission } from '@/lib/parrainage';
import {
  FEEXPAY_MIN_AMOUNT,
  computeFeexPayAmount,
  getFeexPayActiveCountries,
  getFeexPayGatewayCurrency,
  isFeexPayAmountValid,
  resolveFeexPayNetworks,
  resolveXofRateFor,
  type FeexPayConfig,
} from '@/lib/feexpay';
import { fetchSubscriptionPlans } from '@/lib/subscriptionPlans';
import { convertAmount, normalizeCurrency } from '@/lib/currency';
import { logPaymentAttempt } from '@/lib/paymentAttempts';
import { logger } from '@/lib/logger';
import FeexPayModal, { type FeexPayWidgetState } from '@/components/feature/FeexPayModal';

interface Plan {
  key: string;
  name: string;
  /** Prix mensuel (référence DHS/MAD). */
  monthly: number;
  /** Prix annuel explicite en DHS. null = 10 × mensuel. */
  annual?: number | null;
  features: string[];
  description: string;
  moisGratuits: number;
  personnalise: boolean;
}

interface PaymentProvider {
  provider: string;
  label: string;
  enabled: boolean;
  config?: Record<string, unknown> | null;
  public_key?: string | null;
}

const FALLBACK_PLANS: Plan[] = [
  { key: 'starter', name: 'Starter', monthly: 35, annual: null, features: ['1 boutique en ligne', 'Produits illimités', 'Paiement en ligne', 'Support par email'], description: 'Pour démarrer votre activité en ligne', moisGratuits: 0, personnalise: false },
  { key: 'pro', name: 'Pro', monthly: 50, annual: null, features: ['3 boutiques', 'Marketplace', 'Applications avancées', 'Support prioritaire'], description: 'Pour les commerces qui grandissent', moisGratuits: 0, personnalise: false },
  { key: 'business', name: 'Business', monthly: 100, annual: null, features: ['Boutiques illimitées', 'Nom de domaine personnalisé', 'API & intégrations', 'Accompagnement dédié'], description: 'Pour les entreprises ambitieuses', moisGratuits: 0, personnalise: false },
  { key: 'personnalise', name: 'Personnalisé', monthly: 0, annual: null, features: [], description: 'Sur devis — contactez le service client Zifek', moisGratuits: 0, personnalise: true },
];

/**
 * Récupère le message d'erreur réel renvoyé par une Edge Function
 * (supabase-js masque le corps de la réponse derrière `context`).
 */
async function readFunctionError(error: unknown, fallback: string): Promise<string> {
  const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = await ctx.json();
      if (body?.error) return String(body.error);
    } catch {
      /* corps illisible */
    }
  }
  const message = (error as { message?: string })?.message;
  if (message && !/non-2xx/i.test(message)) return message;
  return fallback;
}

/**
 * Message lisible à partir de n'importe quelle erreur.
 * Les erreurs PostgREST renvoyées par le SDK sont de simples objets (pas des
 * instances d'Error) : sans cela, l'utilisateur voyait « Une erreur est
 * survenue. » alors que la base expliquait précisément le problème.
 */
function messageOf(err: unknown, fallback: string): string {
  if (!err) return fallback;
  if (typeof err === 'string') return err || fallback;
  const candidate = err as { message?: string; details?: string; hint?: string; error?: string; code?: string };
  const raw = candidate.message || candidate.error || candidate.details || candidate.hint;
  if (raw && String(raw).trim()) {
    return candidate.code ? `${String(raw)} (code ${candidate.code})` : String(raw);
  }
  return fallback;
}

const PROVIDER_ICONS: Record<string, string> = {
  stripe: 'ri-bank-card-line',
  paypal: 'ri-paypal-line',
  cmi: 'ri-bank-line',
  wave: 'ri-water-flash-line',
  flutterwave: 'ri-flashlight-line',
  feexpay: 'ri-smartphone-line',
};

/** Motifs de refus FeexPay connus → explication compréhensible par le marchand. */
const GATEWAY_REASON_LABELS: Record<string, string> = {
  LOW_BALANCE_OR_PAYEE_LIMIT_REACHED_OR_NOT_ALLOWED:
    'Fonds insuffisants ou plafond atteint sur le compte Mobile Money.',
  INSUFFICIENT_FUNDS: 'Fonds insuffisants sur le compte Mobile Money.',
  INVALID_PHONE_NUMBER: 'Numéro Mobile Money invalide.',
  PAYER_NOT_FOUND: 'Compte Mobile Money introuvable.',
  PAYER_NOT_ALLOWED: 'Ce compte Mobile Money n’est pas autorisé à payer.',
  TRANSACTION_NOT_ALLOWED: 'Transaction non autorisée par l’opérateur.',
  USER_CANCELLED: 'Paiement annulé sur le téléphone.',
  TIMEOUT: 'Délai dépassé : la demande n’a pas été validée sur le téléphone.',
};

interface GatewayFailure {
  message: string;
  reason: string;
  code: string;
}

/**
 * Extrait le motif réel d'un refus, quelle que soit la forme de la réponse :
 * résultat du SDK FeexPay, réponse de notre backend, ou simple message.
 */
function readGatewayFailure(...sources: unknown[]): GatewayFailure {
  const merged: Record<string, unknown> = {};
  sources.forEach((source) => {
    if (!source) return;
    if (typeof source === 'string') {
      if (!merged.message) merged.message = source;
      return;
    }
    if (typeof source === 'object') {
      const obj = source as Record<string, unknown>;
      const nested = (obj.data && typeof obj.data === 'object' ? obj.data : {}) as Record<string, unknown>;
      Object.assign(merged, nested, obj);
    }
  });

  const text = (keys: string[]): string => {
    for (const key of keys) {
      const value = merged[key];
      if (value != null && String(value).trim() !== '') return String(value).trim();
    }
    return '';
  };

  const reason = text(['reason', 'error_code', 'code']);
  const message = text(['message', 'error_message', 'detail', 'gatewayMessage']);
  const gatewayReason = text(['gatewayReason']);
  return {
    message,
    reason: gatewayReason || reason,
    code: (gatewayReason || reason).toUpperCase(),
  };
}

/** Phrase affichée au marchand (vide si aucun détail exploitable). */
function gatewayFailureText(failure: GatewayFailure): string {
  const message = failure.message && !/^(failed|failure|declined|error|refusé)$/i.test(failure.message)
    ? failure.message
    : '';
  if (message) return message;
  const known = GATEWAY_REASON_LABELS[failure.code];
  if (known) return known;
  if (failure.reason) return `Motif opérateur : ${failure.reason}.`;
  return '';
}

/** Mots-clés de réussite / d'échec renvoyés par le SDK FeexPay. */
const WIDGET_SUCCESS = new Set(['success', 'successful', 'paid', 'completed', 'complete', 'approved', 'ok', 'succeeded', '00', '0000']);
const WIDGET_FAILURE = new Set(['failed', 'failure', 'declined', 'cancelled', 'canceled', 'expired', 'rejected', 'error', 'refused', 'refuse', 'reversed']);

interface WidgetOutcome {
  success: boolean;
  failure: boolean;
  transactionReference: string;
  message: string;
}

/**
 * Lit le résultat renvoyé par le SDK FeexPay (dont la forme n'est pas garantie).
 * On y cherche un statut exploitable et l'identifiant de la transaction, pour
 * pouvoir confirmer le paiement même quand la passerelle n'expose pas encore
 * la référence interne de Zifek.
 */
function readWidgetOutcome(result: unknown): WidgetOutcome {
  const statuses: string[] = [];
  const refs: string[] = [];
  let message = '';

  const walk = (value: unknown, depth: number) => {
    if (value == null || depth > 3) return;
    if (typeof value === 'string') {
      if (!message && value.trim()) message = value.trim();
      return;
    }
    if (Array.isArray(value)) {
      value.slice(0, 5).forEach((item) => walk(item, depth + 1));
      return;
    }
    if (typeof value !== 'object') return;
    Object.entries(value as Record<string, unknown>).forEach(([key, raw]) => {
      const k = key.toLowerCase();
      if (['status', 'state', 'statut', 'transaction_status', 'payment_status'].includes(k)
        && (typeof raw === 'string' || typeof raw === 'number')) {
        statuses.push(String(raw).trim().toLowerCase());
      }
      if (['reference', 'transaction_reference', 'transactionid', 'transaction_id', 'txn_id', 'payment_id', 'pay_id', 'referenceid'].includes(k)
        && (typeof raw === 'string' || typeof raw === 'number')) {
        const value2 = String(raw).trim();
        if (value2 && !/^(ZIFSUB|SHOP)-/i.test(value2)) refs.push(value2);
      }
      walk(raw, depth + 1);
    });
  };

  walk(result, 0);
  const success = statuses.some((s) => WIDGET_SUCCESS.has(s));
  const failure = !success && statuses.some((s) => WIDGET_FAILURE.has(s));
  return { success, failure, transactionReference: refs[0] || '', message };
}

export default function AbonnementPage({ embedded = false }: { embedded?: boolean }) {
  const { user } = useAuth();
  // Reprise d'une tentative échouée depuis le journal des paiements :
  // /dashboard/abonnement/choisir?retry=1&plan=Starter&method=feexpay
  const [searchParams] = useSearchParams();
  const retryPlan = searchParams.get('plan') || '';
  const retryMethod = searchParams.get('method') || '';
  const retryCountry = searchParams.get('country') || '';
  const retryNetwork = searchParams.get('network') || '';
  const isRetry = searchParams.get('retry') === '1';
  const [plans, setPlans] = useState<Plan[]>(FALLBACK_PLANS);
  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [loading, setLoading] = useState(true);

  // Abonnement en cours du marchand (affiché en haut : formule, échéance, upgrade).
  const [currentSub, setCurrentSub] = useState<{
    plan: string;
    period: string;
    nextBilling: string | null;
    price: number;
    currency: string;
  } | null>(null);
  const plansRef = useRef<HTMLDivElement>(null);

  const [planKey, setPlanKey] = useState('pro');
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [method, setMethod] = useState<string>('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Vrai quand le refus vient vraiment du paiement (permet d'afficher
  // « Réessayer le paiement » avec la même formule et le même opérateur).
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [done, setDone] = useState(false);
  const [paidConfirmed, setPaidConfirmed] = useState(false);
  const [feexConfig, setFeexConfig] = useState<FeexPayConfig | null>(null);
  const [feexShopId, setFeexShopId] = useState('');
  const [feexToken, setFeexToken] = useState('');
  const [feexWidget, setFeexWidget] = useState<FeexPayWidgetState | null>(null);
  const [widgetVerifying, setWidgetVerifying] = useState(false);
  const [fxCountry, setFxCountry] = useState('');
  const [fxNetwork, setFxNetwork] = useState('');
  const [fxMethod, setFxMethod] = useState<'mobile_money' | 'card'>('mobile_money');
  const [pending, setPending] = useState<{ reference: string; phone: string; amount: number; currency: string; subscriptionId: number } | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState<{ type: 'ok' | 'err' | 'info'; text: string } | null>(null);

  useEffect(() => {
    document.title = 'Abonnement Zifek';
  }, []);

  useEffect(() => {
    (async () => {
      try {
        // Formules (table package) — pilotées par le SuperAdmin.
        const dbPlans = await fetchSubscriptionPlans({ activeOnly: true });
        if (dbPlans.length > 0) {
          const mapped: Plan[] = dbPlans.map((p) => ({
            key: String(p.id),
            name: p.nom,
            monthly: p.prix,
            annual: p.prixAnnuel,
            features: p.features,
            description: p.description,
            moisGratuits: p.moisGratuits,
            personnalise: p.personnalise,
          }));
          setPlans(mapped);
          setPlanKey(mapped[1]?.key || mapped[0].key);
        }

        // Moyens de paiement activés par le superadmin
        const { data: prov } = await supabase
          .from('payment_providers')
          .select('provider, label, enabled, config, public_key')
          .eq('enabled', true)
          .order('sort_order');
        const list = (prov || []) as PaymentProvider[];
        setProviders(list);

        // Configuration FeexPay (pays/opérateurs) pour le sélecteur.
        const feex = list.find((p) => p.provider === 'feexpay');
        if (feex) {
          const cfg = (feex.config || {}) as FeexPayConfig;
          setFeexConfig(cfg);
          // Identifiants de la modale FeexPay (boutique + jeton du SDK).
          setFeexShopId(String(feex.public_key || '').trim());
          setFeexToken(String(cfg.api_key || '').trim());
          const active = getFeexPayActiveCountries(cfg);
          const defaultCountry = String(cfg.country || '') || active[0]?.code || '';
          setFxCountry(defaultCountry);
          const nets = resolveFeexPayNetworks(cfg).filter((o) => o.enabled && o.country === defaultCountry);
          const defaultNetwork = String(cfg.network || '');
          setFxNetwork(nets.some((o) => o.slug === defaultNetwork) ? defaultNetwork : (nets[0]?.slug || ''));
        }
      } catch { /* garde les valeurs par défaut */ }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setName(user.name || '');
    }
  }, [user]);

  // Charge l'abonnement réellement payé (statut « paid ») pour afficher la
  // formule en cours, sa date d'échéance et proposer l'augmentation de forfait.
  useEffect(() => {
    if (!user?.id) { setCurrentSub(null); return; }
    let active = true;
    void (async () => {
      try {
        const { data } = await supabase
          .from('package_subscriptions')
          .select('plan, period, next_billing_date, prix, monaie, status')
          .eq('user_id', user.id)
          .eq('status', 'paid')
          .order('next_billing_date', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (!active) return;
        if (!data) { setCurrentSub(null); return; }
        setCurrentSub({
          plan: String(data.plan || ''),
          period: String(data.period || 'monthly'),
          nextBilling: (data.next_billing_date as string) || null,
          price: Number(data.prix) || 0,
          currency: normalizeCurrency((data.monaie as string) || user.monaie || 'MAD'),
        });
      } catch {
        if (active) setCurrentSub(null);
      }
    })();
    return () => { active = false; };
  }, [user?.id, user?.monaie]);

  // Formule de la tentative échouée : présélection automatique.
  useEffect(() => {
    if (!retryPlan) return;
    const wanted = retryPlan.trim().toLowerCase();
    const match = plans.find((p) => p.key === retryPlan || p.name.trim().toLowerCase() === wanted);
    if (match) setPlanKey(match.key);
  }, [plans, retryPlan]);

  // Moyen de paiement de la tentative échouée : présélection automatique.
  useEffect(() => {
    if (!retryMethod) return;
    if (providers.some((p) => p.provider === retryMethod)) {
      setMethod(retryMethod);
      if (retryMethod === 'feexpay') setFxMethod('mobile_money');
    }
  }, [providers, retryMethod]);

  // Reprise du pays / opérateur choisis lors de la tentative échouée.
  useEffect(() => {
    if (!retryCountry) return;
    const active = getFeexPayActiveCountries(feexConfig);
    if (!active.some((c) => c.code === retryCountry)) return;
    setFxCountry(retryCountry);
    const nets = resolveFeexPayNetworks(feexConfig).filter((o) => o.enabled && o.country === retryCountry);
    if (retryNetwork && nets.some((o) => o.slug === retryNetwork)) setFxNetwork(retryNetwork);
  }, [retryCountry, retryNetwork, feexConfig]);

  const selectedPlan = plans.find((p) => p.key === planKey) || plans[0];
  // Les formules sont stockées en DHS (MAD) : on les convertit dans la devise du commerce.
  const merchantCurrency = normalizeCurrency(user?.monaie || 'MAD');
  const toMerchant = (madAmount: number): number => {
    const raw = Number(madAmount) || 0;
    if (merchantCurrency === 'MAD') return Math.round(raw * 100) / 100;
    const converted = convertAmount(raw, 'MAD', merchantCurrency);
    return Math.round((converted != null ? converted : raw) * 100) / 100;
  };
  /** Affichage monétaire qui préserve les centimes (0,5 MAD reste 0,5). */
  const formatAmount = (value: number): string =>
    (Number(value) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
  const planAnnual = selectedPlan.annual != null && Number(selectedPlan.annual) > 0
    ? Number(selectedPlan.annual)
    : selectedPlan.monthly * 10;
  const price = toMerchant(period === 'monthly' ? selectedPlan.monthly : planAnnual);

  const feexCountries = getFeexPayActiveCountries(feexConfig);
  const feexOperators = resolveFeexPayNetworks(feexConfig).filter((o) => o.enabled && o.country === fxCountry);
  const feexCardEnabled = Boolean(feexConfig?.card_enabled);
  // ⚠️ Taux unique source de vérité : on résout d'abord le taux propre à la
  // devise (xof_rates), puis le taux par défaut. Sans cela, la page et le
  // serveur pouvaient convertir avec deux taux différents (montant refusé).
  const xofRate = resolveXofRateFor(merchantCurrency, feexConfig);
  // Devise réellement encaissée par FeexPay pour le pays choisi :
  // Afrique centrale (Congo, Gabon…) → XAF, Afrique de l'Ouest → XOF.
  const gatewayCurrency = getFeexPayGatewayCurrency(fxCountry);
  const xofAmount = computeFeexPayAmount(price, xofRate, merchantCurrency, undefined, gatewayCurrency);

  /** Lien de reprise : même formule + même moyen de paiement + même opérateur. */
  const retryHref = `${embedded ? '/dashboard/abonnement/choisir' : '/abonnement'}?retry=1&plan=${encodeURIComponent(selectedPlan.name)}&method=feexpay&country=${encodeURIComponent(fxCountry)}&network=${encodeURIComponent(fxNetwork)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPaymentFailed(false);
    if (!method) {
      setError('Merci de choisir un moyen de paiement.');
      return;
    }
    if (!email.trim()) {
      setError('Merci d’indiquer votre email.');
      return;
    }

    setSubmitting(true);
    try {
      const today = new Date();
      const nextBilling = new Date(today);
      const freeMonths = (selectedPlan.moisGratuits || 0) + (user?.parrainage_mois_gratuits || 0);
      if (period === 'monthly') nextBilling.setMonth(nextBilling.getMonth() + 1 + freeMonths);
      else nextBilling.setMonth(nextBilling.getMonth() + 12 + freeMonths);

      const fmt = (d: Date) => d.toISOString().split('T')[0];

      // FeexPay : on valide TOUT avant d'enregistrer la demande, pour ne pas
      // laisser de ligne « en attente » orpheline quand la saisie est incomplète.
      if (method === 'feexpay') {
        if (fxMethod === 'mobile_money') {
          if (!fxCountry || !fxNetwork) {
            setError('Merci de choisir un pays et un opérateur Mobile Money.');
            setSubmitting(false);
            return;
          }
          if (!phone.trim()) {
            setError('Merci d’indiquer votre numéro Mobile Money.');
            setSubmitting(false);
            return;
          }
        }
        if (!isFeexPayAmountValid(xofAmount)) {
          setError(xofAmount < FEEXPAY_MIN_AMOUNT
            ? `Le montant à payer (${xofAmount.toLocaleString()} ${gatewayCurrency}) est inférieur au minimum FeexPay de ${FEEXPAY_MIN_AMOUNT} ${gatewayCurrency}. Choisissez une formule plus élevée ou réglez par carte bancaire.`
            : `Le montant à payer (${xofAmount.toLocaleString()} ${gatewayCurrency}) dépasse le maximum autorisé par FeexPay. Contactez le support.`);
          setSubmitting(false);
          return;
        }
      }

      // On crée d'abord la demande d'abonnement (en attente) afin de pouvoir
      // l'apparier au retour de paiement FeexPay via une référence unique.
      // FeexPay : on note le sous-moyen (Mobile Money / Carte) pour le suivi
      // côté SuperAdmin (page « Mes abonnements »).
      const methodLabelForNote = method === 'feexpay'
        ? `FeexPay ${fxMethod === 'card' ? 'Carte bancaire' : 'Mobile Money'}`
        : method;

      const { data: inserted, error: insertError } = await supabase
        .from('package_subscriptions')
        .insert({
          user_id: user?.id ?? 0,
          plan: selectedPlan.name,
          period,
          prix: price,
          monaie: merchantCurrency,
          start_date: fmt(today),
          next_billing_date: fmt(nextBilling),
          status: 'pending',
          payment_method: method,
          note: `Demande d'abonnement — ${name || '—'} (${email}) · ${methodLabelForNote}`,
        })
        .select('id')
        .maybeSingle();
      if (insertError) throw insertError;
      const subscriptionId = Number(inserted?.id || 0);

      // FeexPay : initiation du paiement réel (si activé et configuré).
      let feexpayUrl: string | null = null;
      if (method === 'feexpay') {
        const reference = subscriptionId
          ? `ZIFSUB-${subscriptionId}-${Date.now()}`
          : `ZIFSUB-0-${Date.now()}`;
        if (subscriptionId) {
          await supabase
            .from('package_subscriptions')
            .update({ last_payment_id: reference })
            .eq('id', subscriptionId);
        }

        // ── Modale FeexPay directement dans la page : le client choisit son
        // pays, son opérateur et valide sur son téléphone sans quitter Zifek.
        // On retombe sur la page hébergée uniquement si les identifiants
        // FeexPay (Shop ID / jeton) sont absents.
        if (feexShopId && feexToken && subscriptionId) {
          setFeexWidget({
            reference,
            subscriptionId,
            amount: xofAmount,
            currency: gatewayCurrency,
            shopId: feexShopId,
            token: feexToken,
            description: `Abonnement ${selectedPlan.name}`,
            payCase: fxMethod === 'card' ? 'CARD' : 'MOBILE',
            firstName: name || email.split('@')[0] || 'Client',
            email,
            phone: fxMethod === 'card' ? '' : phone,
            country: fxCountry,
            network: fxMethod === 'card' ? '' : feexOperators.find((o) => o.slug === fxNetwork)?.label?.split(' ')[0] || '',
          });
          setSubmitting(false);
          return;
        }
        try {
          const { data, error: fnError } = await supabase.functions.invoke('feexpay-checkout', {
            body: {
              amount: price,
              currency: merchantCurrency,
              gatewayCurrency,
              country: fxCountry,
              network: fxNetwork,
              method: fxMethod,
              rate: xofRate,
              plan: selectedPlan.name,
              period,
              email,
              name,
              phone,
              subscriptionId,
              reference,
            },
          });
          if (fnError) {
            const fallback = (data as { error?: string } | null)?.error;
            throw new Error(await readFunctionError(fnError, fallback || 'Impossible d’initier le paiement FeexPay.'));
          }
          const res = data as { url?: string | null; reference?: string; currency?: string; amount?: number } | null;
          if (res?.url) {
            feexpayUrl = res.url;
          } else {
            // Push USSD : le client valide sur son téléphone.
            logger.info('paiement', 'Demande FeexPay envoyée (Mobile Money)', {
              montant: price,
              devise: merchantCurrency,
              montant_envoye: xofAmount,
              operateur: fxNetwork,
              pays: fxCountry,
              formule: selectedPlan.name,
              reference: res?.reference || reference,
            });
            setPending({
              reference: res?.reference || reference,
              phone: fxMethod === 'card' ? 'Carte bancaire' : phone,
              amount: Number(res?.amount) || xofAmount,
              currency: res?.currency || gatewayCurrency,
              subscriptionId,
            });
            setSubmitting(false);
            return;
          }
        } catch (err) {
          // La demande d'abonnement ne doit pas rester « en attente » quand le
          // paiement n'a jamais pu être initié : on la clôture.
          if (subscriptionId) {
            await supabase
              .from('package_subscriptions')
              .update({ status: 'failed', note: `Paiement FeexPay non initié — ${name || '—'} (${email})` })
              .eq('id', subscriptionId);
          }
          const realMessage = messageOf(err, 'Impossible d’initier le paiement FeexPay.');
          // Journal des paiements : montant, devise, opérateur et message réel.
          void logPaymentAttempt({
            provider: 'feexpay',
            flow: 'subscription',
            amount: price,
            currency: merchantCurrency,
            gatewayAmount: xofAmount,
            gatewayCurrency,
            operator: fxMethod === 'card' ? 'card' : fxNetwork,
            country: fxCountry,
            phone,
            subscriptionId,
            errorMessage: realMessage,
            rawResponse: (err as { message?: string })?.message,
            requestPayload: {
              plan: selectedPlan.name,
              period,
              method: fxMethod,
              network: fxNetwork,
              country: fxCountry,
            },
          });
          setError(realMessage);
          setSubmitting(false);
          return;
        }
      }

      // Parrainage : pour un paiement en ligne direct (hors FeexPay, dont la
      // commission est créditée par le callback de paiement), on enregistre la
      // commission du parrain immédiatement (best-effort).
      if (method !== 'feexpay' && user?.id) {
        void recordReferralCommission({
          filleulId: user.id,
          filleulName: name || user.name,
          plan: selectedPlan.name,
          montant: price,
        });
      }

      // FeexPay : redirection vers la page de paiement sécurisée.
      if (feexpayUrl) {
        window.location.assign(feexpayUrl);
        return;
      }

      setDone(true);
      logger.info('paiement', `Demande d’abonnement enregistrée (${method})`, {
        montant: price,
        devise: merchantCurrency,
        formule: selectedPlan.name,
        abonnement: subscriptionId,
      });
    } catch (err: unknown) {
      setError(messageOf(err, 'Une erreur est survenue. Réessayez dans un instant.'));
    } finally {
      setSubmitting(false);
    }
  };

  /** Vérifie auprès du backend si le paiement est déjà confirmé (sans attendre). */
  const checkStatus = async () => {
    if (!pending || checking) return;
    setChecking(true);
    setCheckMsg(null);
    try {
      const { data, error } = await supabase.functions.invoke('feexpay-status', {
        body: { subscriptionId: pending.subscriptionId, reference: pending.reference },
      });
      if (error) throw error;
      const payload = data as { status?: string; gatewayMessage?: string; gatewayReason?: string } | null;
      const status = payload?.status;
      if (status === 'paid') {
        setCheckMsg({ type: 'ok', text: 'Paiement confirmé ! Votre abonnement est activé.' });
        setPaidConfirmed(true);
        setTimeout(() => setDone(true), 1200);
      } else if (status === 'failed') {
        // On affiche le motif réel renvoyé par l'opérateur (fonds insuffisants,
        // plafond atteint, numéro invalide…) au lieu d'un message générique.
        const detail = gatewayFailureText({
          message: String(payload?.gatewayMessage || ''),
          reason: String(payload?.gatewayReason || ''),
          code: String(payload?.gatewayReason || '').toUpperCase(),
        });
        setPaymentFailed(true);
        setCheckMsg({
          type: 'err',
          text: detail
            ? `Paiement refusé — ${detail} Vérifiez votre solde puis réessayez.`
            : 'Le paiement a été refusé. Vous pouvez réessayer.',
        });
      } else if (status === 'not_found') {
        setCheckMsg({ type: 'info', text: 'Aucune transaction trouvée pour cette référence. Le paiement est peut-être encore en cours.' });
      } else {
        setCheckMsg({ type: 'info', text: 'Le paiement n’est pas encore confirmé. Validez la demande sur votre téléphone, puis réessayez.' });
      }
    } catch (err) {
      setCheckMsg({ type: 'err', text: err instanceof Error ? err.message : 'Vérification impossible pour le moment.' });
    } finally {
      setChecking(false);
    }
  };

  /**
   * Retour de la modale FeexPay : on ne se contente pas du message du widget,
   * on interroge notre backend qui vérifie la transaction auprès de FeexPay
   * (le webhook peut arriver plus tard, ou jamais).
   */
  const handleWidgetResult = async (result: unknown) => {
    const widget = feexWidget;
    if (!widget) return;
    setWidgetVerifying(true);
    setError(null);
    setCheckMsg(null);
    const outcome = readWidgetOutcome(result);
    let lastStatus = 'pending';
    let failure: GatewayFailure = { message: '', reason: '', code: '' };
    // Le SDK FeexPay ne remonte « success » qu'après un paiement réellement
    // validé sur le téléphone : on transmet cet indice au backend pour qu'il
    // confirme l'abonnement même si la passerelle n'expose pas encore la
    // transaction (webhook non reçu, référence inconnue côté FeexPay…).
    const widgetStatus = outcome.success ? 'success' : outcome.failure ? 'failed' : '';

    for (let attempt = 0; attempt < 4; attempt += 1) {
      try {
        const { data } = await supabase.functions.invoke('feexpay-status', {
          body: {
            subscriptionId: widget.subscriptionId,
            reference: widget.reference,
            widgetStatus,
            transactionReference: outcome.transactionReference,
          },
        });
        const payload = data as {
          status?: string;
          gatewayMessage?: string;
          gatewayReason?: string;
        } | null;
        const status = String(payload?.status || '');
        lastStatus = status || 'pending';
        if (status === 'paid') {
          setWidgetVerifying(false);
          setFeexWidget(null);
          setPaidConfirmed(true);
          setDone(true);
          return;
        }
        if (status === 'failed') {
          failure = readGatewayFailure(
            { message: payload?.gatewayMessage || '', reason: payload?.gatewayReason || '', gatewayReason: payload?.gatewayReason || '' },
            result,
          );
          break;
        }
      } catch {
        /* on retente */
      }
      await new Promise((resolve) => { window.setTimeout(resolve, 2500); });
    }

    setWidgetVerifying(false);
    setFeexWidget(null);
    if (lastStatus === 'failed') {
      const detail = failure.message ? gatewayFailureText(failure) : gatewayFailureText(readGatewayFailure(result));
      setPaymentFailed(true);
      setError(detail
        ? `Paiement refusé — ${detail} Vérifiez votre solde puis réessayez.`
        : 'Le paiement a été refusé ou annulé. Vérifiez votre solde Mobile Money puis réessayez.');
      void logPaymentAttempt({
        provider: 'feexpay',
        flow: 'subscription',
        amount: price,
        currency: merchantCurrency,
        gatewayAmount: widget.amount,
        gatewayCurrency: widget.currency,
        operator: widget.payCase === 'CARD' ? 'card' : fxNetwork,
        country: widget.country,
        phone,
        subscriptionId: widget.subscriptionId,
        reference: widget.reference,
        errorCode: failure.reason || null,
        errorMessage: detail ? `Paiement refusé — ${detail}` : 'Paiement refusé via la modale FeexPay.',
        rawResponse: typeof result === 'object' ? JSON.stringify(result).slice(0, 1000) : String(result ?? ''),
      });
      return;
    }

    setPending({
      reference: widget.reference,
      phone: widget.payCase === 'CARD' ? 'Carte bancaire' : phone,
      amount: widget.amount,
      currency: widget.currency,
      subscriptionId: widget.subscriptionId,
    });
  };

  /** Repli : page de paiement FeexPay hébergée, si la modale ne s’affiche pas. */
  const openHostedCheckout = async () => {
    const widget = feexWidget;
    if (!widget) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('feexpay-checkout', {
        body: {
          amount: price,
          currency: merchantCurrency,
          gatewayCurrency: widget.currency,
          country: fxCountry,
          network: fxNetwork,
          method: fxMethod,
          rate: xofRate,
          plan: selectedPlan.name,
          period,
          email,
          name,
          phone,
          subscriptionId: widget.subscriptionId,
          reference: widget.reference,
        },
      });
      if (fnError) throw new Error(await readFunctionError(fnError, 'Impossible d’ouvrir la page FeexPay.'));
      const res = data as { url?: string | null; reference?: string; currency?: string; amount?: number } | null;
      if (res?.url) {
        window.location.assign(res.url);
        return;
      }
      setFeexWidget(null);
      setPending({
        reference: res?.reference || widget.reference,
        phone: fxMethod === 'card' ? 'Carte bancaire' : phone,
        amount: Number(res?.amount) || widget.amount,
        currency: res?.currency || widget.currency,
        subscriptionId: widget.subscriptionId,
      });
    } catch (err) {
      setError(messageOf(err, 'Impossible d’ouvrir la page FeexPay.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (pending) {
    return (
      <div className="bg-background-50 flex items-center justify-center px-4 py-16 min-h-[60vh]">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-smartphone-line text-3xl text-accent-600"></i>
          </div>
          <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-2">Paiement en cours</h1>
          <p className="text-sm text-foreground-500 mb-5">
            Une demande de paiement de <strong>{pending.amount.toLocaleString()} {pending.currency}</strong> a été envoyée au{' '}
            <strong>{pending.phone}</strong>. Validez-la directement sur votre téléphone (message Mobile Money).
          </p>
          <div className="px-4 py-3 bg-background-100/70 rounded-lg text-xs text-foreground-600 mb-6 text-left space-y-1">
            <p className="flex items-center justify-between gap-3"><span>Formule</span><span className="font-medium text-foreground-800">{selectedPlan.name} · {period === 'monthly' ? 'mensuel' : 'annuel'}</span></p>
            <p className="flex items-center justify-between gap-3"><span>Référence</span><span className="font-mono text-foreground-800 break-all">{pending.reference}</span></p>
          </div>
          <p className="text-xs text-foreground-400 mb-4">
            Votre abonnement sera activé automatiquement dès la confirmation du paiement.
          </p>
          {checkMsg && (
            <div className={`mb-4 px-4 py-3 rounded-lg text-sm text-left flex items-center gap-2 ${
              checkMsg.type === 'ok'
                ? 'bg-accent-50 text-accent-700 border border-accent-200'
                : checkMsg.type === 'err'
                  ? 'bg-red-50 text-red-600 border border-red-200'
                  : 'bg-background-100 text-foreground-600 border border-background-200/70'
            }`}>
              <i className={checkMsg.type === 'ok' ? 'ri-checkbox-circle-line' : checkMsg.type === 'err' ? 'ri-error-warning-line' : 'ri-information-line'}></i>
              {checkMsg.text}
            </div>
          )}
          {checkMsg?.type === 'err' && (
            <Link
              to={retryHref}
              className="mb-4 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-red-600 text-white text-sm font-semibold no-underline whitespace-nowrap hover:bg-red-700 transition-colors"
            >
              <i className="ri-restart-line"></i>
              Réessayer le paiement
            </Link>
          )}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={checkStatus}
              disabled={checking}
              className="w-full sm:w-auto px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-bold hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {checking ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-refresh-line"></i>}
              Vérifier le statut
            </button>
            <Link
              to={embedded ? '/dashboard/abonnement' : '/'}
              className="w-full sm:w-auto px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium no-underline hover:bg-background-200/70 transition-colors whitespace-nowrap"
            >
              Retour à l&apos;accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="bg-background-50 flex items-center justify-center px-4 py-16 min-h-[60vh]">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-3xl text-accent-600"></i>
          </div>
          <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-2">
            {paidConfirmed ? 'Abonnement activé !' : 'Demande enregistrée !'}
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            {paidConfirmed ? (
              <>
                Votre paiement FeexPay a été confirmé. La formule <strong>{selectedPlan.name}</strong>{' '}
                ({period === 'monthly' ? 'mensuelle' : 'annuelle'}) est désormais active.
              </>
            ) : (
              <>
                Votre demande d&apos;abonnement <strong>{selectedPlan.name}</strong> ({period === 'monthly' ? 'mensuel' : 'annuel'}) via{' '}
                <strong>{providers.find((p) => p.provider === method)?.label || method}</strong> a bien été reçue.
                Nous vous contacterons pour finaliser le paiement.
              </>
            )}
          </p>
          <Link
            to={embedded ? '/dashboard/abonnement' : '/'}
            className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Retour à l&apos;accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background-50">
      <section className="relative py-8 md:py-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/50 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
            Abonnement Zifek
          </h1>
          <p className="text-sm md:text-base text-foreground-500">
            Choisissez votre formule et réglez en ligne avec le moyen de paiement de votre choix.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
          {isRetry && (
            <div className="mb-5 flex items-start gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50">
              <span className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-restart-line text-sm"></i>
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground-900">Nouvelle tentative de paiement</p>
                <p className="text-[11px] text-foreground-600 mt-0.5">
                  Votre tentative précédente n’a pas abouti. La formule et le moyen de paiement sont déjà
                  présélectionnés : vérifiez votre numéro Mobile Money puis validez à nouveau.
                </p>
              </div>
            </div>
          )}
          {/* Abonnement en cours (statut payé) */}
          {currentSub && (
            <div className="mb-6 rounded-xl border border-accent-200/70 bg-accent-50/50 p-4 md:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-vip-crown-2-line text-lg"></i>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-accent-700">Votre formule actuelle</p>
                  <p className="text-base font-bold font-heading text-foreground-950">
                    {currentSub.plan}
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-200/60 text-accent-900 text-[10px] font-bold align-middle">
                      <i className="ri-checkbox-circle-fill text-[10px]"></i>
                      Payé
                    </span>
                  </p>
                  <p className="text-xs text-foreground-600 mt-0.5">
                    {currentSub.period === 'yearly' ? 'Facturation annuelle' : 'Facturation mensuelle'}
                    {currentSub.nextBilling && (
                      <>
                        {' · '}
                        <span className="font-medium text-foreground-800">
                          expire le {new Date(currentSub.nextBilling).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                      </>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const idx = plans.findIndex((p) => p.name.trim().toLowerCase() === currentSub.plan.trim().toLowerCase());
                    const next = plans.slice(idx + 1).find((p) => !p.personnalise) || plans[idx];
                    if (next) setPlanKey(next.key);
                    plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-up-circle-line"></i>
                  Augmenter mon forfait
                </button>
              </div>
            </div>
          )}

          {/* Période */}
          <div ref={plansRef} className="flex justify-center mb-6">
            <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-1">
              <button
                type="button"
                onClick={() => setPeriod('monthly')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  period === 'monthly' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500'
                }`}
              >
                Mensuel
              </button>
              <button
                type="button"
                onClick={() => setPeriod('yearly')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  period === 'yearly' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500'
                }`}
              >
                Annuel <span className="text-accent-600 text-xs">(-2 mois)</span>
              </button>
            </div>
          </div>

          {/* Plans */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {plans.map((plan) => (
              <button
                key={plan.key}
                type="button"
                onClick={() => setPlanKey(plan.key)}
                className={`text-left p-5 rounded-xl border transition-colors cursor-pointer ${
                  planKey === plan.key ? 'border-primary-300 bg-primary-50/50 ring-1 ring-primary-200' : 'border-background-200/70 bg-background-50 hover:border-background-300/60'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-foreground-900">{plan.name}</span>
                  {planKey === plan.key && <i className="ri-checkbox-circle-fill text-primary-500"></i>}
                </div>
                <div className="text-2xl font-bold text-foreground-950 mb-1">
                  {plan.personnalise ? (
                    <span className="text-lg">Sur devis</span>
                  ) : (
                    <>{formatAmount(toMerchant(plan.monthly))} <span className="text-sm font-medium text-foreground-500">{merchantCurrency}/mois</span></>
                  )}
                </div>
                {!plan.personnalise && (
                  <div className="text-[11px] text-foreground-500 mb-1">
                    Annuel : <span className="font-semibold text-foreground-700">{formatAmount(toMerchant(plan.annual != null && Number(plan.annual) > 0 ? Number(plan.annual) : plan.monthly * 10))} {merchantCurrency}</span>
                  </div>
                )}
                {plan.moisGratuits > 0 && (
                  <div className="text-xs text-accent-600 font-medium mb-1.5 flex items-center gap-1">
                    <i className="ri-gift-line"></i>
                    {plan.moisGratuits} mois offert{plan.moisGratuits > 1 ? 's' : ''}
                  </div>
                )}
                {plan.description && (
                  <p className="text-xs text-foreground-500 mb-3 line-clamp-2">{plan.description}</p>
                )}
                <ul className="space-y-1.5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-1.5 text-xs text-foreground-600">
                      <i className="ri-check-line text-accent-500 mt-0.5"></i>
                      {f}
                    </li>
                  ))}
                </ul>
              </button>
            ))}
          </div>

          {/* Parrainage : mois gratuits offerts */}
          {(user?.parrainage_mois_gratuits || 0) > 0 && (
            <div className="flex items-start gap-3 px-4 py-3 bg-accent-50 border border-accent-200 rounded-lg mb-6">
              <div className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-gift-line"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-900">
                  Vous bénéficiez de {user?.parrainage_mois_gratuits} mois gratuit{(user?.parrainage_mois_gratuits || 0) > 1 ? 's' : ''}
                </p>
                <p className="text-xs text-foreground-600 mt-0.5">
                  Offerts par votre parrainage{user?.code_parrainage ? ` (code ${user.code_parrainage})` : ''}. Ils seront ajoutés à votre première période.
                </p>
              </div>
            </div>
          )}

          {selectedPlan?.personnalise ? (
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-2-line text-2xl text-accent-700"></i>
              </div>
              <h2 className="text-lg font-bold font-heading text-foreground-950 mb-2">Formule personnalisée</h2>
              <p className="text-sm text-foreground-500 max-w-md mx-auto mb-6">
                Cette formule est établie sur devis. Contactez le service client Zifek pour construire une
                offre adaptée à votre activité.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/dashboard/messages"
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline whitespace-nowrap hover:bg-primary-600 transition-colors flex items-center gap-2"
                >
                  <i className="ri-chat-3-line"></i>
                  Contacter le service client
                </Link>
                <Link
                  to={embedded ? '/dashboard/abonnement' : '/'}
                  className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium no-underline whitespace-nowrap hover:bg-background-200/70 transition-colors"
                >
                  Retour à l&apos;accueil
                </Link>
              </div>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 space-y-5">
            {/* Coordonnées */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="abo-name" className="block text-sm font-medium text-foreground-700 mb-1.5">Nom</label>
                <input
                  id="abo-name"
                  name="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Votre nom"
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
              </div>
              <div>
                <label htmlFor="abo-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="abo-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  required
                />
              </div>
            </div>

            {/* Moyens de paiement */}
            <div>
              <p className="text-sm font-medium text-foreground-700 mb-2">Moyen de paiement</p>
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-foreground-400">
                  <i className="ri-loader-4-line animate-spin"></i> Chargement des moyens de paiement...
                </div>
              ) : providers.length === 0 ? (
                <div className="px-4 py-3 bg-background-100 rounded-lg text-sm text-foreground-500 flex items-center gap-2">
                  <i className="ri-information-line"></i>
                  Aucun moyen de paiement n&apos;est disponible pour le moment. Revenez bientôt.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {providers.map((p) => (
                    <button
                      key={p.provider}
                      type="button"
                      onClick={() => setMethod(p.provider)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors cursor-pointer ${
                        method === p.provider
                          ? 'border-primary-300 bg-primary-50/60 text-primary-700'
                          : 'border-background-200/70 bg-background-50 text-foreground-700 hover:border-background-300/60'
                      }`}
                    >
                      <i className={`${PROVIDER_ICONS[p.provider] || 'ri-bank-card-line'} text-base`}></i>
                      {p.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* FeexPay : Mobile Money ou Carte bancaire */}
            {method === 'feexpay' && (
              <div className="rounded-xl border border-background-200/70 bg-background-100/40 p-4 space-y-4">
                {feexCardEnabled && (
                  <div className="flex items-center gap-0.5 bg-background-50 border border-background-200/70 rounded-full p-1 w-fit">
                    <button
                      type="button"
                      onClick={() => setFxMethod('mobile_money')}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                        fxMethod === 'mobile_money' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <i className="ri-smartphone-line mr-1.5"></i>Mobile Money
                    </button>
                    <button
                      type="button"
                      onClick={() => setFxMethod('card')}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                        fxMethod === 'card' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <i className="ri-bank-card-line mr-1.5"></i>Carte bancaire
                    </button>
                  </div>
                )}

                {fxMethod === 'mobile_money' ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="abo-country" className="block text-sm font-medium text-foreground-700 mb-1.5">Pays</label>
                        <select
                          id="abo-country"
                          value={fxCountry}
                          onChange={(e) => {
                            const c = e.target.value;
                            setFxCountry(c);
                            const first = resolveFeexPayNetworks(feexConfig).find((o) => o.enabled && o.country === c);
                            setFxNetwork(first?.slug || '');
                          }}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                        >
                          {feexCountries.length === 0 && <option value="">— Pays à configurer —</option>}
                          {feexCountries.map((c) => (
                            <option key={c.code} value={c.code}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="abo-network" className="block text-sm font-medium text-foreground-700 mb-1.5">Opérateur</label>
                        <select
                          id="abo-network"
                          value={fxNetwork}
                          onChange={(e) => setFxNetwork(e.target.value)}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                        >
                          {feexOperators.length === 0 && <option value="">— Aucun opérateur —</option>}
                          {feexOperators.map((o) => (
                            <option key={`${o.country}-${o.slug}`} value={o.slug}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="abo-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        Numéro Mobile Money <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="abo-phone"
                        name="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Ex. 01 XX XX XX XX"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-3 flex-wrap text-xs">
                      <span className="text-foreground-500 flex items-center gap-1">
                        <i className="ri-smartphone-line"></i>
                        Vous recevrez une demande de paiement à valider sur votre téléphone.
                      </span>
                      <span className="font-semibold text-foreground-800 whitespace-nowrap">
                        ≈ {xofAmount.toLocaleString()} {gatewayCurrency}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="flex items-start gap-3 text-sm text-foreground-600">
                    <i className="ri-secure-payment-line text-primary-500 text-lg mt-0.5"></i>
                    <div>
                      <p className="font-medium text-foreground-800">Paiement sécurisé par carte bancaire</p>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        La fenêtre de paiement FeexPay s’ouvre sur cette page.
                        Montant : <span className="font-semibold text-foreground-800">≈ {xofAmount.toLocaleString()} {gatewayCurrency}</span>.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Récapitulatif */}
            <div className="flex items-center justify-between px-4 py-3 bg-background-100/70 rounded-lg">
              <span className="text-sm text-foreground-600">
                {selectedPlan.name} · {period === 'monthly' ? 'mensuel' : 'annuel'}
              </span>
              <span className="text-lg font-bold text-primary-600">{formatAmount(price)} {merchantCurrency}</span>
            </div>

            {error && (
              <div className="px-4 py-3 bg-red-50 text-red-600 rounded-lg text-sm space-y-2">
                <div className="flex items-start gap-2">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  <span className="flex-1">{error}</span>
                </div>
                {paymentFailed && (
                  <Link
                    to={retryHref}
                    onClick={() => { setError(null); setPaymentFailed(false); }}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white text-xs font-semibold whitespace-nowrap no-underline hover:bg-red-700 transition-colors"
                  >
                    <i className="ri-restart-line"></i>
                    Réessayer le paiement
                  </Link>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || providers.length === 0}
              className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-bold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {submitting ? (
                <><i className="ri-loader-4-line animate-spin"></i>Envoi...</>
              ) : method === 'feexpay' && feexShopId && feexToken ? (
                <><i className="ri-secure-payment-line"></i>Payer avec FeexPay</>
              ) : (
                <><i className="ri-secure-payment-line"></i>Valider mon abonnement</>
              )}
            </button>
          </form>
          )}

          {/* Modale FeexPay (SDK officiel) : s’ouvre dans la page, sans redirection */}
          {method === 'feexpay' && feexWidget && (
            <div className="mt-4 rounded-xl border border-background-200/70 bg-background-50 p-4">
              {widgetVerifying ? (
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <i className="ri-loader-4-line animate-spin text-primary-500"></i>
                  Vérification du paiement auprès de FeexPay…
                </div>
              ) : (
                <FeexPayModal
                  state={feexWidget}
                  onResult={(result) => { void handleWidgetResult(result); }}
                  onFallback={() => { void openHostedCheckout(); }}
                />
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}